import React, { useState } from "react";
import { motion } from "framer-motion";
import { HeartPulse } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO/SEO";
import emailjs from "emailjs-com";

export default function WebHealthCheckService() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ website: "", email: "" });
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await emailjs.send(
        "service_388x50v", // Replace with your EmailJS service ID
        "template_5k4dsbk", // Replace with your EmailJS template ID
        { website: formData.website, email: formData.email },
        "6pRF5XaBmj1du2b9k" // Replace with your EmailJS user ID
      );
      setResponse("Thank you! We have received your request. You will get a report soon.");
    } catch (error) {
      setResponse("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="bg-gray-950 text-white min-h-screen py-12 px-2 md:px-12 relative overflow-hidden">
      <SEO 
        title="Free Website Health Check | Mahmudur Rahman"
        description="Check your website's performance, security, and SEO with our free website health check service."
        image="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740500597/1111111_dpb6bw.jpg"
        url="https://devplus.fun/campaigns/free-website-health-check"
        keywords="Full Stack Developer, MERN Stack, React, Node.js, MongoDB, Web Development, JavaScript, Next.js, Express.js, website health check, free website health check, website analysis, website audit, website performance, website SEO, website mobile responsiveness, website optimization, website health checkup"
        author="Mahmudur Rahman"
      />

      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-950 opacity-80"></div>
      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute top-10 left-10 w-24 h-24 bg-green-500 opacity-30 rounded-full blur-3xl"></motion.div>
      <motion.div animate={{ x: [0, 10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-10 right-10 w-32 h-32 bg-blue-500 opacity-20 rounded-full blur-3xl"></motion.div>

      {/* Ribbon Banner */}
      <div className="absolute top-20 z-10 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-blue-500 text-white px-6 py-2 rounded-full shadow-lg text-sm font-semibold uppercase tracking-wide">
        Limited Time Free Offer
      </div>

      {/* Banner */}
      <div className="relative text-center mt-10 py-20 bg-cover bg-center rounded-xl shadow-lg" style={{ backgroundImage: "url('https://res.cloudinary.com/dqovjmmlx/image/upload/v1740500597/1111111_dpb6bw.jpg')" }}>
        <div className="absolute inset-0 bg-black bg-opacity-70 rounded-xl"></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="lg:text-4xl text-2xl font-bold">Free Website Health Check</h1>
          <p className="text-gray-300 mt-2">Analyze your website's performance, SEO, and mobile responsiveness.</p>
        </div>
      </div>

      {/* Service Details */}
      <div className="relative z-10 max-w-3xl mx-auto mt-8 text-center">
        <HeartPulse className="text-red-500 w-16 h-16 mx-auto animate-pulse" />
        <h2 className="text-2xl font-semibold mt-4">Basic Website Health Check</h2>
        <p className="text-gray-400 mt-2">
          Get a professional analysis of your website's speed, SEO optimization, and mobile responsiveness. Identify critical areas for improvement.
        </p>
        <span className="bg-green-500 text-white font-bold text-sm px-3 py-1 rounded-full inline-block mt-4 shadow-md">FREE</span>
      </div>

      {/* Booking Form */}
      <div className="relative z-10 max-w-lg mx-auto mt-10 bg-gray-800 p-6 rounded-xl shadow-2xl">
        <h3 className="text-xl font-semibold text-center mb-4">Submit Your Website</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="url"
            name="website"
            placeholder="Enter website URL"
            required
            className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:ring-2 focus:ring-green-500 focus:outline-none shadow-md"
            value={formData.website}
            onChange={handleChange}
          />
          <input
            type="email"
            name="email"
            placeholder="Your email (for report)"
            required
            className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:ring-2 focus:ring-green-500 focus:outline-none shadow-md"
            value={formData.email}
            onChange={handleChange}
          />
          <motion.button 
            whileHover={{ scale: 1.05 }} 
            type="submit" 
            className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg shadow-md transition-all duration-300 flex justify-center items-center gap-2">
            {loading ? "Sending..." : "Get Free Check"}
          </motion.button>
        </form>
        {response && <p className="text-green-400 text-center mt-4 animate-fade-in">{response}</p>}
      </div>
    </div>
  );
}